"use client";

import { useState } from "react";
import type { VitrineEstimatorConfig } from "@/lib/vitrine/types";

// Démo "estimateur" : base + options choisies, affichée en fourchette. Le
// barème est un EXEMPLE et le dit en permanence (décision 2026-09-27).

function round(n: number): number {
  if (n >= 10_000) return Math.round(n / 500) * 500;
  if (n >= 1_000) return Math.round(n / 50) * 50;
  return Math.round(n / 5) * 5;
}

export function DemoEstimator({
  config,
  onInteract,
  onComplete,
}: {
  config: VitrineEstimatorConfig;
  onInteract: () => void;
  onComplete: () => void;
}) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const complete = config.questions.every((q) => answers[q.id] !== undefined);
  const total =
    config.base + config.questions.reduce((sum, q) => sum + (q.options[answers[q.id]]?.valeur ?? 0), 0);
  const spread = (total * config.fourchettePct) / 100 / 2;
  const fmt = (n: number) => `${new Intl.NumberFormat("fr-FR").format(round(n))} ${config.unite}`;

  const pick = (qid: string, idx: number) => {
    onInteract();
    const next = { ...answers, [qid]: idx };
    setAnswers(next);
    if (config.questions.every((q) => next[q.id] !== undefined)) onComplete();
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-display text-2xl text-ink">{config.titre}</h3>
        {config.intro && <p className="mt-1 text-sm text-ink/60">{config.intro}</p>}
      </div>
      {config.questions.map((q, qi) => (
        <fieldset key={q.id}>
          <legend className="mb-2 text-sm font-medium text-ink/80">
            {qi + 1}. {q.label}
          </legend>
          <div className="flex flex-wrap gap-2">
            {q.options.map((o, oi) => {
              const on = answers[q.id] === oi;
              return (
                <button
                  key={oi}
                  type="button"
                  onClick={() => pick(q.id, oi)}
                  aria-pressed={on}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    on ? "border-gold bg-gold/10 text-ink" : "border-line text-ink/70 hover:border-ink/30"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div className="rounded-xl bg-navy-deep p-5 text-paper">
        <div className="text-sm text-paper/70">{config.resultatLabel}</div>
        <div className="mt-1 font-display text-3xl">
          {complete ? `${fmt(total - spread)} à ${fmt(total + spread)}` : "Répondez aux questions"}
        </div>
      </div>
      <p className="text-xs text-ink/50">
        Barème d&apos;exemple pour la démonstration : sur votre site, c&apos;est vous qui fixez les montants,
        depuis votre espace d&apos;administration.
      </p>
    </div>
  );
}
